"use client";

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import _ from 'lodash';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import CloseButton from './CloseButton';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { User, MapPin, MessageSquare, ChevronRight, Tag, X } from '@/components/icons';
import { endOfDay, format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { es } from 'date-fns/locale';
import { Link, useLocation } from 'react-router-dom';
import ImageViewer from './ImageViewer';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { Session } from '@supabase/supabase-js';
import { getCommentsByNoteId, getNoteById } from '@/lib/supabase';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { cn, truncateLocationName } from '@/lib/utils'; // Importar truncateLocationName
import { NoteSidebar } from './NoteSidebar';
import { Eye } from 'lucide-react';
import PromoScoreDisplay from './PromoScoreDisplay';

interface NoteItem {
  id: string;
  user_id: string;
  title: string;
  content: string;
  photo_url: string | null;
  image_urls?: string[] | null;
  view_count?: number;
  upvotes?: number;
  downvotes?: number;
  created_at: string;
  updated_at: string | null;
  is_edited: boolean;
  location_display_name: string | null;
  latitude: number | null;
  longitude: number | null;
  profiles: {
    username: string;
    avatar_url: string | null;
  } | null;
  category_id: string | null;
  category_name: string | null;
}

interface Comment {
  id: string;
  created_at: string;
  content: string;
  user_id: string;
  profiles: {
    username: string;
    avatar_url: string | null;
  } | null;
}

interface NoteCardProps {
  note: NoteItem;
  currentUserId: string | undefined;
  onDelete: (noteId: string) => void;
  session?: Session | null;
  onCollectionChange?: () => void;
}


const NoteCard: React.FC<NoteCardProps> = ({ note, currentUserId, onDelete, session, onCollectionChange }) => {
  const [views, setViews] = useState(note.view_count ?? 0);
  const { noteSidebarOpen, setNoteSidebarOpen, setSelectedNote, setShowNoteClickOverlay, showNoteClickOverlay } = useContentSidebar();
  const channelRef = useRef<any>(null);
    // Suscribirse al canal de difusión para view_count
    useEffect(() => {
      const channel = supabase.channel(`note_views_${note.id}`);
      channelRef.current = channel;
      const sub = channel
        .on('broadcast', { event: 'vistas_actualizadas' }, ({ payload }) => {
          if (typeof payload?.new_count === 'number') {
            setViews(payload.new_count);
          }
        })
        .subscribe();
      return () => {
        channel.unsubscribe();
      };
    }, [note.id]);

    // Función para enviar la actualización (throttle 5s)
    const enviarActualizacion = useCallback(
      _.throttle((nuevoConteo: number) => {
        if (channelRef.current) {
          channelRef.current.send({
            type: 'broadcast',
            event: 'vistas_actualizadas',
            payload: { new_count: nuevoConteo },
          });
        }
      }, 5000),
      []
    );

    // Cuando se detecta un cambio real de vistas (por ejemplo, tras registrar una vista y obtener el nuevo count)
    useEffect(() => {
      if (note.view_count !== undefined && note.view_count !== views) {
        setViews(note.view_count);
        enviarActualizacion(note.view_count);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [note.view_count]);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [viewerImageUrls, setViewerImageUrls] = useState<string[] | null>(null);
  const [localNote, setLocalNote] = useState<NoteItem>(note);
  const [totalCommentsCount, setTotalCommentsCount] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const canEdit = currentUserId === note.user_id && new Date() < endOfDay(new Date(note.created_at));
  const createdAtDate = new Date(note.created_at);
  const updatedAtDate = note.updated_at ? new Date(note.updated_at) : null;
  const publishedString = `${format(createdAtDate, "dd/MM/yyyy HH:mm:ss", { locale: es })}`;
  // full date/time for modal and single-page views
  const dateFull = note.is_edited && updatedAtDate
    ? `${publishedString} (Actualizado ${format(updatedAtDate, 'HH:mm:ss', { locale: es })})`
    : publishedString;
  // relative date for feed/list views
  const dateRelative = formatDistanceToNow(createdAtDate, { addSuffix: true, locale: es });

  // Small display for list/header: keep relative times for today, "ayer HH:mm" for yesterday,
  // otherwise show full published date/time.
  const smallDate = isToday(createdAtDate)
    ? dateRelative
    : isYesterday(createdAtDate)
      ? `ayer ${format(createdAtDate, 'HH:mm', { locale: es })}`
      : publishedString;

  const fetchCommentCount = useCallback(async () => {
    try {
      const allComments = await getCommentsByNoteId(note.id);
      setTotalCommentsCount(allComments.length);
    } catch (err) {
      console.error('[NoteCard fetchCommentCount] Failed:', err);
      // Fail silently to avoid crash, count remains null/default
    }
  }, [note.id]);

  useEffect(() => {
    fetchCommentCount();
  }, [fetchCommentCount]);

  // Cerrar sidebar cuando cambia la ruta (solo cuando la ruta cambia realmente, no en otros re-renders)
  const prevPathname = useRef(location.pathname);
  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      setNoteSidebarOpen(false);
      prevPathname.current = location.pathname;
    }
  }, [location.pathname, setNoteSidebarOpen]);

  useEffect(() => {
    if (contentRef.current) {
      const timer = setTimeout(() => {
        try {
          setIsTruncated(contentRef.current!.scrollHeight > contentRef.current!.clientHeight);
        } catch (e) { setIsTruncated(false); }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [note.content, isExpanded]);

  // Detect if the date wrapped to a new line relative to the username
  

  

  const handleImageClick = (url: string, allUrls?: string[]) => {
    // open viewer for all images, starting at the clicked one
    const urls = allUrls && allUrls.length > 0 ? allUrls : [url];
    setViewerImageUrls(urls);
    setSelectedImageIndex(urls.indexOf(url) >= 0 ? urls.indexOf(url) : 0);
    setSelectedImageUrl(url);
    setIsImageViewerOpen(true);
  }

  const handleCloseImageViewer = () => {
    setIsImageViewerOpen(false);
    setSelectedImageUrl(null);
    setViewerImageUrls(null);
    setSelectedImageIndex(0);
  };

  // Lógica para truncar la ubicación usando la utilidad
  const truncatedLocationName = truncateLocationName(note.location_display_name);

  // Helper: strip HTML to produce a plain-text excerpt
  const stripHtml = (html: string) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  };

  const EXCERPT_LENGTH = 240;
  const plain = stripHtml(note.content || '');
  const excerpt = plain.length > EXCERPT_LENGTH ? `${plain.slice(0, EXCERPT_LENGTH).trim()}…` : plain;

  // Helper to open note sidebar
  const saveScrollAndNavigate = async (href: string) => {
    try {
      // Extract note ID from path
      const noteId = href.split('/').pop();
      if (noteId) {
        const fullNote = await getNoteById(noteId);
        if (fullNote) {
          setSelectedNote(fullNote);
          setNoteSidebarOpen(true);
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to load note', e);
    }
  };

  // Elimina la llamada a incrementNoteViewCount aquí, solo navega
  const handleOpenNote = async (href: string) => {
    try {
      const noteId = href.split('/').pop();
      if (noteId) {
        // Setear overlay INMEDIATAMENTE
        setShowNoteClickOverlay(true);
        
        // Usar la nota parcial mientras se carga la completa
        const partialNote = { ...note, id: noteId };
        setSelectedNote(partialNote);
        setNoteSidebarOpen(true);
        
        // Luego cargar la nota completa en background
        const fullNote = await getNoteById(noteId);
        if (fullNote) {
          // Actualizar con los datos completos
          setSelectedNote(fullNote);
        }
        return;
      }
    } catch (e) {
      console.warn('Failed to load note', e);
    }
  };

  // Debug logging removed for production
  return (
    <>
      {/* Overlay seteado por click en card */}
      {showNoteClickOverlay && (
        <div className="fixed inset-0 bg-black/20 z-[9996] transition-opacity duration-200 pointer-events-none" />
      )}
      
      <Card ref={cardRef as any} className="p-4 sm:p-5 flex flex-col gap-4 w-full max-w-full relative">
      {/* --- TÍTULO --- */}
      <button
        type="button"
        onClick={() => handleOpenNote(`/notas/${note.id}`)}
        className="block text-left hover:underline p-0 w-full"
        aria-label={`Abrir nota ${note.title}`}
      >
        <h3 className="text-lg sm:text-xl font-extrabold leading-tight">{note.title}</h3>
      </button>

      {/* --- CONTENT --- */}
      <div className="space-y-3">
        {/* Preview: show truncated plain-text excerpt with fade; clicking title or excerpt opens modal */}
        <div
          ref={contentRef}
          className={cn(
            "prose text-sm text-foreground max-w-none relative leading-relaxed break-words",
            !isExpanded && plain.length > EXCERPT_LENGTH && "excerpt-truncate",
            plain.length <= EXCERPT_LENGTH && "excerpt-no-fade"
          )}
          onClick={() => handleOpenNote(`/notas/${note.id}`)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') handleOpenNote(`/notas/${note.id}`); }}
        >
          <p>{!isExpanded ? excerpt : undefined}</p>
          {isExpanded && (
            <div dangerouslySetInnerHTML={{ __html: note.content }} />
          )}
        </div>
        {/* If content is longer than excerpt, show a small action hint */}
        {plain.length > EXCERPT_LENGTH && (
          <div className="flex items-center gap-2 pt-1">
            <Button variant="link" onClick={() => handleOpenNote(`/notas/${note.id}`)} className="p-0 h-auto text-primary justify-start -mt-1">
              Leer más
            </Button>
          </div>
        )}

        {/* Image carousel: support multiple image urls (note.image_urls) or fallback to single photo_url */}
        {(() => {
          // Try multiple sources: note.image_urls (array) or note.photo_url (single)
          const maybeAny = (note as any).image_urls || null;
          const urls: string[] = Array.isArray(maybeAny) && maybeAny.length > 0 ? maybeAny : (note.photo_url ? [note.photo_url] : []);
          
          // Image URLs resolved successfully
          
          if (urls.length === 0) return null;

          return (
            <div className="mt-2">
              <Carousel className="w-full">
                <CarouselContent>
                  {urls.map((imageUrl, idx) => (
                    <CarouselItem key={idx}>
                        <div className="w-full rounded-md overflow-hidden">
                          <div className="relative w-full aspect-video">
                          <img
                            src={imageUrl}
                            alt={note.title}
                            className="absolute inset-0 w-full h-full object-cover object-center cursor-pointer block rounded-md antialiased active:scale-95 active:shadow-inner transition-transform"
                            onClick={() => handleImageClick(imageUrl, urls)}
                            onLoad={() => {
                              try { window.dispatchEvent(new CustomEvent('note-image-loaded')); } catch (e) {}
                            }}
                            onError={() => {
                              try { window.dispatchEvent(new CustomEvent('note-image-loaded')); } catch (e) {}
                            }}
                          />
                        </div>
                      </div>
                      
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {urls.length > 1 && (
                  <>
                    <CarouselPrevious />
                    <CarouselNext />
                  </>
                )}
              </Carousel>
            </div>
          );
        })()}
      </div>

      {/* Voting Score Display - Minimalista en la card */}
      <PromoScoreDisplay 
        upvotes={note.upvotes || 0}
        downvotes={note.downvotes || 0}
        categoryName={note.category_name}
      />

      {/* Bloque del autor removido - se abre sidebar al hacer clic en la nota */}

      <ImageViewer
        imageUrl={viewerImageUrls ? null : selectedImageUrl}
        imageUrls={viewerImageUrls ?? (selectedImageUrl ? [selectedImageUrl] : undefined)}
        startIndex={selectedImageIndex}
        isOpen={isImageViewerOpen}
        onClose={handleCloseImageViewer}
      />
    </Card>
    </>
  );
};

export default NoteCard;